/**
 * AttachmentChips — the files staged above the composer: an image's local
 * thumbnail or a kind icon, the name (cut in the middle), the size, a progress
 * ring while it uploads, a red failed state, and × to remove it.
 */
import { useCallback, useSyncExternalStore } from 'react';

import { WarningCircle, X } from '../../../design-system/icons';
import { formatBytes, middleTruncate } from '../../../lib/format';
import { cn } from '../../../lib/utils';
import { COMPOSER_CHIP_DISMISS_CLASS } from './chatClasses';
import { FileKindIcon } from './FileKindIcon';
import { removeFileLabel, UPLOAD_FAILED_LABEL, type StagedFileState, type UploadProgressSource } from './moments';

const CHIP_TESTID = 'attachment-chip';
const NAME_MAX_CHARS = 28;
const KIND_ICON_PX = 18;
const FAILED_ICON_PX = 12;
const DISMISS_ICON_PX = 12;

const MIN_PROGRESS = 0;
const MAX_PROGRESS = 1;
const PERCENT_MIN = 0;
const PERCENT_MAX = 100;

const RING_SIZE_PX = 24;
const RING_STROKE_PX = 2.5;
const RING_CENTER_PX = RING_SIZE_PX / 2;
const RING_RADIUS_PX = (RING_SIZE_PX - RING_STROKE_PX) / 2;
const RING_CIRCUMFERENCE_PX = 2 * Math.PI * RING_RADIUS_PX;
const RING_VIEW_BOX = `0 0 ${RING_SIZE_PX} ${RING_SIZE_PX}`;
const RING_FILL = 'none';
const RING_LINECAP = 'round';

const LIST_CLASS = 'mb-2 flex flex-wrap gap-2';
const CHIP_CLASS = 'flex min-w-0 max-w-full items-center gap-2 rounded-[6px] border border-border bg-muted/60 py-1.5 pl-1.5 pr-1';
const FAILED_CHIP_CLASS = 'border-rose-300 bg-rose-50';
const PREVIEW_CLASS =
  'relative flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-[4px] border border-border bg-card text-muted-foreground';
const THUMB_CLASS = 'size-full object-cover';
const TEXT_CLASS = 'flex min-w-0 flex-col';
const NAME_CLASS = 'truncate text-caption-medium text-foreground';
const SIZE_CLASS = 'flex items-center gap-1 text-[11px]/4 text-muted-foreground';
const FAILED_SIZE_CLASS = 'text-rose-600';
const SCREEN_READER_ONLY_CLASS = 'sr-only';
const RING_OVERLAY_CLASS = 'absolute inset-0 flex items-center justify-center bg-black/35';
const RING_SVG_CLASS = '-rotate-90';
const RING_TRACK_CLASS = 'stroke-white/40';
const RING_BAR_CLASS = 'stroke-white';

function uploadingLabel(name: string): string {
  return `Uploading ${name}`;
}

function toPercent(progress: number): number {
  const clamped = Math.min(Math.max(progress, MIN_PROGRESS), MAX_PROGRESS);
  return Math.round(clamped * PERCENT_MAX);
}

function unsubscribeNothing(): void {
  // No live source: nothing to stop listening to.
}

function subscribeNothing(): () => void {
  return unsubscribeNothing;
}

/** This chip's live progress, re-rendering only this chip when it moves. */
function useLiveProgress(id: string, source: UploadProgressSource | undefined): number | undefined {
  const subscribe = source?.subscribe ?? subscribeNothing;
  const read = useCallback(() => source?.get(id), [source, id]);
  return useSyncExternalStore(subscribe, read, read);
}

/**
 * A failed chip shows its failed state instead of a ring, and a file without
 * `progress` shows none; otherwise the live value wins over the one the file
 * was staged with.
 */
function uploadProgress(file: StagedFileState, live: number | undefined): number | undefined {
  if (file.failed || file.progress === undefined) return undefined;
  return live ?? file.progress;
}

interface ProgressRingProps {
  progress: number;
  label: string;
}

function ProgressRing({ progress, label }: ProgressRingProps) {
  const percent = toPercent(progress);
  const dashOffset = RING_CIRCUMFERENCE_PX * (MAX_PROGRESS - percent / PERCENT_MAX);
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={PERCENT_MIN}
      aria-valuemax={PERCENT_MAX}
      aria-valuenow={percent}
      className={RING_OVERLAY_CLASS}
    >
      <svg width={RING_SIZE_PX} height={RING_SIZE_PX} viewBox={RING_VIEW_BOX} className={RING_SVG_CLASS} aria-hidden>
        <circle
          cx={RING_CENTER_PX}
          cy={RING_CENTER_PX}
          r={RING_RADIUS_PX}
          fill={RING_FILL}
          strokeWidth={RING_STROKE_PX}
          className={RING_TRACK_CLASS}
        />
        <circle
          cx={RING_CENTER_PX}
          cy={RING_CENTER_PX}
          r={RING_RADIUS_PX}
          fill={RING_FILL}
          strokeWidth={RING_STROKE_PX}
          strokeDasharray={RING_CIRCUMFERENCE_PX}
          strokeDashoffset={dashOffset}
          strokeLinecap={RING_LINECAP}
          className={RING_BAR_CLASS}
        />
      </svg>
    </div>
  );
}

interface FilePreviewProps {
  file: StagedFileState;
  progressSource?: UploadProgressSource;
}

function FilePreview({ file, progressSource }: FilePreviewProps) {
  const progress = uploadProgress(file, useLiveProgress(file.id, progressSource));
  return (
    <div className={PREVIEW_CLASS}>
      {/* Decorative: the name beside it already identifies the file. */}
      {file.previewUrl ? <img src={file.previewUrl} alt="" className={THUMB_CLASS} /> : <FileKindIcon kind={file.kind} size={KIND_ICON_PX} aria-hidden />}
      {progress === undefined ? null : <ProgressRing progress={progress} label={uploadingLabel(file.name)} />}
    </div>
  );
}

function FailedMark() {
  return (
    <>
      <WarningCircle size={FAILED_ICON_PX} aria-hidden />
      <span className={SCREEN_READER_ONLY_CLASS}>{UPLOAD_FAILED_LABEL}</span>
    </>
  );
}

interface AttachmentChipProps {
  file: StagedFileState;
  progressSource?: UploadProgressSource;
  onRemove?: (id: string) => void;
  removeDisabled: boolean;
}

function AttachmentChip({ file, progressSource, onRemove, removeDisabled }: AttachmentChipProps) {
  const removeFile = (): void => {
    onRemove?.(file.id);
  };
  return (
    <li
      className={cn(CHIP_CLASS, file.failed && FAILED_CHIP_CLASS)}
      data-testid={CHIP_TESTID}
      data-failed={file.failed || undefined}
    >
      <FilePreview file={file} progressSource={progressSource} />
      <div className={TEXT_CLASS}>
        <span className={NAME_CLASS} title={file.name}>
          {middleTruncate(file.name, NAME_MAX_CHARS)}
        </span>
        <span className={cn(SIZE_CLASS, file.failed && FAILED_SIZE_CLASS)}>
          {file.failed ? <FailedMark /> : null}
          {formatBytes(file.file.size)}
        </span>
      </div>
      {onRemove ? (
        <button
          type="button"
          onClick={removeFile}
          disabled={removeDisabled}
          aria-label={removeFileLabel(file.name)}
          className={COMPOSER_CHIP_DISMISS_CLASS}
        >
          <X size={DISMISS_ICON_PX} />
        </button>
      ) : null}
    </li>
  );
}

export interface AttachmentChipsProps {
  files: StagedFileState[];
  /** The chips' live upload progress, read per chip; absent → each file's own `progress`. */
  progressSource?: UploadProgressSource;
  /** × on a chip; omitted → chips can't be removed from here. */
  onRemove?: (id: string) => void;
  /** A send in flight already took the files it carries, so removing one now wouldn't unsend it. */
  removeDisabled?: boolean;
}

export function AttachmentChips({ files, progressSource, onRemove, removeDisabled = false }: AttachmentChipsProps) {
  return (
    <ul className={LIST_CLASS}>
      {files.map((file) => (
        <AttachmentChip key={file.id} file={file} progressSource={progressSource} onRemove={onRemove} removeDisabled={removeDisabled} />
      ))}
    </ul>
  );
}
